import throttle from 'lodash/throttle';
import { useEffect, useRef, useState } from 'react';

import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { NEW_NAV_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { setIsHeaderVisible } from 'store/slice/pageLayoutSlice';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

/**
 * [useToggleHeaderOnScrollEvent]
 * listens for scroll events, and
 * when scrolling down, hides the header
 * when scrolling up displays the header again
 */

export const useToggleHeaderOnScrollEvent = (isHomepage = false, preventToggle = false) => {
  const storeDispatch = useStoreDispatch();
  const prevScrollY = useRef(0);
  const [scrollingDown, setScrollingDown] = useState(true);
  const isBannerVisible = useIsBannerVisible();
  const { isMobile } = useWindowSize();
  const HEADER_HEIGHT = Number.parseInt(NEW_NAV_MOBILE_HEADER_HEIGHT, 10);
  const BANNER_HEIGHT = Number.parseInt(MOBILE_BANNER_HEIGHT, 10);
  const height = isBannerVisible ? HEADER_HEIGHT + BANNER_HEIGHT : HEADER_HEIGHT;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;

      if (isHomepage && isMobile) return;
      if (preventToggle) return;

      if (
        prevScrollY.current < currentScrollY &&
        Math.abs(prevScrollY.current - currentScrollY) > height &&
        scrollingDown
      ) {
        storeDispatch(setIsHeaderVisible(false));
        setScrollingDown(false);
      }

      if (prevScrollY.current > currentScrollY && !scrollingDown) {
        storeDispatch(setIsHeaderVisible(true));
        setScrollingDown(true);
      }

      prevScrollY.current = currentScrollY;
    };

    const throttledScrollHandler = throttle(handleScroll, 100);

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [scrollingDown, isMobile, preventToggle]);
};
